import React from 'react';
import { motion } from 'framer-motion';
import { Building2, MapPin, Coins, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const ProjectsSection = () => {
  const projects = [
    {
      id: 'taiyo-residences',
      name: 'Taiyo Residences',
      type: 'Apartment',
      location: 'Wasl Gate',
      developer: 'LMD',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/Taiyo_Residences_ycqbg3.png',
      price: 'Starting from AED 1,000,000',
      completion: 'Q1 2028',
      features: ['5 mins to SZR and metro', 'Malls closeby', 'Places of worship'],
    },
    {
      id: 'damac-islands',
      name: 'Damac Islands',
      type: 'Townhouse',
      location: 'Dubailand',
      developer: 'DAMAC',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776609/dmaac_island_gv9fyk.png',
      price: 'Starting from AED 2,500,000',
      completion: 'Q4 2028',
      features: ['Lagoon community', 'Located on E611', 'Close to new airport'],
    },
    {
      id: 'hillside-residences',
      name: 'Hillside Residences',
      type: 'Apartment',
      location: 'Wasl Gate',
      developer: 'WASL',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776609/hillside_fsajqw.png',
      price: 'Starting from AED 1,200,000',
      completion: 'Q3 2027',
      features: ['Large gated community', 'Places of worship', 'Next to Festival Plaza'],
    },
    {
      id: 'emaar-the-heights',
      name: 'Emaar The Heights',
      type: 'Villas',
      location: 'Dubai South',
      developer: 'EMAAR',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/the_hieghts_adizsg.png',
      price: 'Starting from AED 6,000,000',
      completion: 'Q3 2029',
      features: ['Wellness based community', 'Close to Maktoum Airport', 'Premium Finish'],
    },
    {
      id: 'south-bay-6',
      name: 'South Bay 6',
      type: 'Villa',
      location: 'Dubai South',
      developer: 'DUBAI SOUTH',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/south_bay_6_e9ltyz.png',
      price: 'Starting from AED 5,000,000',
      completion: 'Q3 2027',
      features: ['Premium semi-detached villa', 'Large lagoon community', 'Luxury Villas'],
    },
    {
      id: 'avenue-residences-5',
      name: 'Avenue Residences 5',
      type: 'Apartment',
      location: 'Furjan',
      developer: 'NABNI',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/5th_avenue_dcovww.png',
      price: 'Starting from AED 1,450,000',
      completion: 'Q1 2026',
      features: ['2 mins to Metro', 'Close to SZR', 'High Quality finish'],
    },
  ];

  return (
    <section id="projects" className="relative py-24 overflow-hidden bg-gradient-to-br from-[#FFF8DC] to-[#F5F5DC]">
      {/* Animated background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 right-0 w-[800px] h-[800px] bg-yellow-50 rounded-full blur-3xl opacity-30"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400/20 border border-yellow-500/30 rounded-full mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Building2 className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-700 font-semibold">Investment Opportunities</span>
          </motion.div>
          
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-gray-800">Exclusive</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
              Projects Portfolio
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Handpicked premium developments offering exceptional ROI and lifestyle
          </p>
        </motion.div>

        {/* Projects grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 100, rotateX: 45 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.15,
                duration: 0.8,
                type: "spring",
                stiffness: 100
              }}
            >
              <Link to={`/project/${project.id}`}>
                <motion.div
                  className="relative h-[650px] rounded-3xl overflow-hidden cursor-pointer"
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.4 }
                  }}
                >
                  {/* Card background */}
                  <div className="absolute inset-0 bg-white border border-gray-200 shadow-lg group-hover:shadow-2xl transition-all duration-500">
                    {/* Image section */}
                    <div className="relative h-[300px] overflow-hidden">
                      <motion.img
                        src={project.image}
                        alt={project.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.15 }}
                        transition={{ duration: 0.6 }}
                      />
                      
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />

                      {/* Developer badge */}
                      <motion.div
                        className="absolute top-4 right-4 px-4 py-2 bg-yellow-400 backdrop-blur-sm rounded-full text-gray-900 font-bold text-sm shadow-lg"
                        initial={{ x: 100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: index * 0.15 + 0.3 }}
                      >
                        {project.developer}
                      </motion.div>

                      {/* Completion date badge */}
                      <motion.div
                        className="absolute top-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-gray-200 flex items-center gap-2"
                        initial={{ x: -100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: index * 0.15 + 0.4 }}
                      >
                        <Calendar className="w-4 h-4 text-yellow-600" />
                        <span className="text-gray-700 text-sm font-medium">{project.completion}</span>
                      </motion.div>
                    </div>

                    {/* Content section */}
                    <div className="p-8 space-y-6">
                      {/* Project name */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.15 + 0.5 }}
                      >
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">
                          {project.name}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm">{project.location}</span>
                        </div>
                      </motion.div>

                      {/* Price */}
                      <motion.div
                        className="flex items-center gap-2"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.15 + 0.6 }}
                      >
                        <Coins className="w-5 h-5 text-yellow-600" />
                        <span className="text-xl font-bold text-yellow-600">
                          {project.price}
                        </span>
                      </motion.div>

                      {/* Features */}
                      <div className="space-y-2">
                        {project.features.map((feature, idx) => (
                          <motion.div
                            key={idx}
                            className="flex items-center gap-2 text-gray-700 text-sm"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.15 + 0.7 + idx * 0.1 }}
                          >
                            <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                            <span>{feature}</span>
                          </motion.div>
                        ))}
                      </div>

                      {/* CTA Button */}
                      <motion.button
                        className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.15 + 0.9 }}
                      >
                        <span>View Details</span>
                        <motion.svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </motion.svg>
                      </motion.button>
                    </div>
                  </div>

                  {/* Hover glow effect */}
                  <motion.div
                    className="absolute inset-0 border-2 border-yellow-400/0 group-hover:border-yellow-400/60 rounded-3xl transition-all duration-500 pointer-events-none"
                  />
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Bottom section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
        >
          <p className="text-gray-600 text-lg mb-6">
            Want to explore more exclusive opportunities?
          </p>
          <motion.a
            href="#contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>Request Full Portfolio</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsSection;
